import dayjs from '@/config/dayjs';
import { parseAppDate } from '@/utils/date';
import { formatNumberToCurrency } from '@/utils/currency';

export type HistoricalAmount = number | null;

export interface HistoricalComparisonAmounts {
  OldDollarBalance: HistoricalAmount;
  DollarBalance: HistoricalAmount;
  DollarChange: HistoricalAmount;
  PercentChange: HistoricalAmount;
}

const isFiniteNumber = (value: HistoricalAmount): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The comparison API has no empty balance. A date with no snapshot is sent
 * as `$0`. That zero is not a real balance when the account was added later.
 */
const accountWasAddedAfter = (
  accountCreatedDate: string | null | undefined,
  asOfDate: string | null | undefined,
): boolean => {
  if (!accountCreatedDate || !asOfDate) return false;
  const created = parseAppDate(accountCreatedDate);
  const asOf = parseAppDate(asOfDate);
  if (!created || !asOf) return false;
  return created.isAfter(asOf, 'day');
};

/**
 * Keep a reported balance unless it is the API's `$0` stand-in for a date
 * before the account existed. A non-zero amount stays, including history
 * entered for a date before the account record was created. A real `$0`
 * on an account that already existed stays `$0`.
 */
export const knownHistoricalBalance = (
  balance: HistoricalAmount,
  accountCreatedDate: string | null | undefined,
  asOfDate: string | null | undefined,
): HistoricalAmount => {
  if (balance !== 0) return balance;
  if (!accountWasAddedAfter(accountCreatedDate, asOfDate)) return balance;
  return null;
};

/**
 * Sum known amounts. An empty set is unavailable (`null`), not `$0`.
 */
export const sumKnownHistoricalAmounts = (
  values: readonly HistoricalAmount[],
): HistoricalAmount => {
  let sum = 0;
  let seen = false;
  for (const value of values) {
    if (!isFiniteNumber(value)) continue;
    sum += value;
    seen = true;
  }
  return seen ? sum : null;
};

/**
 * Dollar and percent change for one account.
 * A missing start or end is not treated as `$0`.
 * Percent change from a real `$0` to a non-zero end is undefined.
 * A real `$0` to `$0` is a `0%` change.
 */
export const resolveHistoricalRowChange = (
  start: HistoricalAmount,
  end: HistoricalAmount,
  reportedDollarChange: HistoricalAmount,
): Pick<HistoricalComparisonAmounts, 'DollarChange' | 'PercentChange'> => {
  if (!isFiniteNumber(start) || !isFiniteNumber(end)) {
    return { DollarChange: null, PercentChange: null };
  }

  const dollarChange = isFiniteNumber(reportedDollarChange)
    ? reportedDollarChange
    : end - start;

  if (start === 0) {
    return {
      DollarChange: dollarChange,
      PercentChange: dollarChange === 0 ? 0 : null,
    };
  }

  return {
    DollarChange: dollarChange,
    PercentChange: (dollarChange / Math.abs(start)) * 100,
  };
};

export const withHistoricalChange = <
  T extends {
    OldDollarBalance: HistoricalAmount;
    DollarBalance: HistoricalAmount;
    DollarChange: HistoricalAmount;
  },
>(
  record: T,
): T & Pick<HistoricalComparisonAmounts, 'DollarChange' | 'PercentChange'> => {
  const change = resolveHistoricalRowChange(
    record.OldDollarBalance,
    record.DollarBalance,
    record.DollarChange,
  );
  return { ...record, ...change };
};

/**
 * True when the comparison end date is today or later.
 * A past end date keeps its historical snapshot.
 */
export const comparisonEndIsCurrent = (
  endDate: string | null | undefined,
): boolean => {
  const parsed = parseAppDate(endDate);
  if (!parsed) return false;
  return !parsed.isBefore(dayjs(), 'day');
};

/**
 * Accounts-page ending balance for one historical row.
 * The accounts list is used when it includes the account. The account open
 * in a single-account view supplies its own balance when that list does not.
 */
export const lookupLiveEndingBalance = (
  rowAccountId: number,
  balancesByAccountId: ReadonlyMap<number, number> | undefined,
  selectedAccountId?: number,
  selectedAccountBalance?: number | null,
): number | undefined => {
  const listed = balancesByAccountId?.get(rowAccountId);
  if (listed !== undefined) return listed;
  if (selectedAccountId !== rowAccountId) return undefined;
  if (
    typeof selectedAccountBalance !== 'number' ||
    !Number.isFinite(selectedAccountBalance)
  ) {
    return undefined;
  }
  return selectedAccountBalance;
};

/**
 * Ending balance for one account.
 * The comparison API sends `$0` when it has no snapshot for that date.
 * On a range that ends today or later, the Accounts page balance is the
 * ending value shown everywhere else. A reported non-zero snapshot is kept.
 * A past end date is left unchanged, including a real `$0`.
 */
export const withLiveEndingBalance = <
  T extends {
    DollarBalance: HistoricalAmount;
    DollarChange: HistoricalAmount;
  },
>(
  record: T,
  liveBalance: number | null | undefined,
  endDate: string | null | undefined,
): T => {
  if (!comparisonEndIsCurrent(endDate)) return record;
  if (typeof liveBalance !== 'number' || !Number.isFinite(liveBalance)) {
    return record;
  }
  if (record.DollarBalance != null && record.DollarBalance !== 0) {
    return record;
  }
  if (record.DollarBalance === liveBalance) return record;
  return {
    ...record,
    DollarBalance: liveBalance,
    DollarChange: null,
  };
};

/**
 * Apply a comparison range to one account.
 * A `$0` on a date before the account was added becomes unavailable, and
 * `$ Change` / `% Change` are not computed from that zero.
 */
export const withHistoricalChangeForRange = <
  T extends {
    OldDollarBalance: HistoricalAmount;
    DollarBalance: HistoricalAmount;
    DollarChange: HistoricalAmount;
  },
>(
  record: T,
  range: {
    createdDate?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  },
): T & Pick<HistoricalComparisonAmounts, 'DollarChange' | 'PercentChange'> =>
  withHistoricalChange({
    ...record,
    OldDollarBalance: knownHistoricalBalance(
      record.OldDollarBalance,
      range.createdDate,
      range.startDate,
    ),
    DollarBalance: knownHistoricalBalance(
      record.DollarBalance,
      range.createdDate,
      range.endDate,
    ),
  });

/**
 * Group and grand totals.
 * Each balance column sums every member with a known amount on that date.
 * Dollar and percent change use members that have both dates, so a missing
 * start is not counted as `$0`.
 */
export const aggregateHistoricalComparisons = (
  rows: readonly HistoricalComparisonAmounts[],
): HistoricalComparisonAmounts => {
  const started = rows.filter((row) => isFiniteNumber(row.OldDollarBalance));
  const complete = started.filter(
    (row) =>
      isFiniteNumber(row.DollarBalance) && isFiniteNumber(row.DollarChange),
  );
  const dollarChange = sumKnownHistoricalAmounts(
    complete.map((row) => row.DollarChange),
  );
  const completeStart = sumKnownHistoricalAmounts(
    complete.map((row) => row.OldDollarBalance),
  );

  let percentChange: HistoricalAmount = null;
  if (complete.length > 0 && dollarChange != null && completeStart != null) {
    percentChange =
      completeStart === 0
        ? dollarChange === 0
          ? 0
          : null
        : (dollarChange / Math.abs(completeStart)) * 100;
  }

  return {
    OldDollarBalance: sumKnownHistoricalAmounts(
      started.map((row) => row.OldDollarBalance),
    ),
    DollarBalance: sumKnownHistoricalAmounts(
      rows.map((row) => row.DollarBalance),
    ),
    DollarChange: dollarChange,
    PercentChange: percentChange,
  };
};

/**
 * Format a historical dollar amount.
 * `emptyLabel` is the translated missing-value marker (`common.emdash`).
 * A real zero stays currency and does not use `emptyLabel`.
 */
export const formatHistoricalCurrency = (
  value: HistoricalAmount | undefined,
  emptyLabel: string,
): string => {
  const amount = value ?? null;
  if (!isFiniteNumber(amount)) return emptyLabel;
  return formatNumberToCurrency(amount);
};

/**
 * Format a historical percent with two decimal places.
 * `emptyLabel` is the translated missing-value marker (`common.emdash`).
 * A real zero stays `0.00%` and does not use `emptyLabel`.
 */
export const formatHistoricalPercentFixed = (
  value: HistoricalAmount | undefined,
  emptyLabel: string,
): string => {
  const amount = value ?? null;
  if (!isFiniteNumber(amount)) return emptyLabel;
  return `${amount.toFixed(2)}%`;
};
